import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  TextInput,
  ActivityIndicator,
  Image,
  Dimensions,
  Platform,
  Animated,
} from 'react-native';
import { db, functions } from '../config/firebase';
import { doc, getDoc, updateDoc, addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import Icon from 'react-native-vector-icons/Feather';

const { width, height } = Dimensions.get('window');
const isWeb = Platform.OS === 'web';
const isMobile = width < 768;

// Colors - matches HandyHawk branding
const colors = {
  darkNavy: '#0A0E14',
  navy: '#13192B',
  navyLight: '#1E2640',
  navyMid: '#252f3f',
  teal: '#41B69A',
  gold: '#d4a84b',
  white: '#FFFFFF',
  text: '#E0E0E0',
  textMuted: '#8B8FA3',
  gray: '#6B7280',
  green: '#10B981',
  red: '#EF4444',
  orange: '#F59E0B',
  glassBorder: 'rgba(255,255,255,0.08)',
};

const InvoicePayScreen = ({ invoiceId }) => {
  const [loading, setLoading] = useState(true);
  const [invoice, setInvoice] = useState(null);
  const [paymentSettings, setPaymentSettings] = useState(null);
  const [error, setError] = useState(null);
  const [step, setStep] = useState(1); // 1: Review, 2: Payment, 3: Success
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [wantsTestimonial, setWantsTestimonial] = useState(false);
  const [rating, setRating] = useState(5);
  const [reviewText, setReviewText] = useState('');
  const fadeAnim = useRef(new Animated.Value(0)).current;
  
  useEffect(() => {
    loadInvoice();
    
    // Fade in animation
    Animated.timing(fadeAnim, {
      toValue: 1,
      duration: 600,
      useNativeDriver: !isWeb,
    }).start();
  }, [invoiceId]);
  
  const loadInvoice = async () => {
    try {
      if (!invoiceId) {
        setError('No invoice ID provided');
        setLoading(false);
        return;
      }
      
      // Load invoice
      const invoiceDoc = await getDoc(doc(db, 'invoices', invoiceId));
      
      if (!invoiceDoc.exists()) {
        setError('Invoice not found');
        setLoading(false);
        return;
      }
      
      const invoiceData = { id: invoiceDoc.id, ...invoiceDoc.data() };
      
      // Check if already paid
      if (invoiceData.status === 'paid') {
        setInvoice(invoiceData);
        setError('paid');
        setLoading(false);
        return;
      }
      
      // Check if expired or cancelled
      if (invoiceData.status === 'cancelled' || invoiceData.status === 'expired') {
        setInvoice(invoiceData);
        setError('invalid');
        setLoading(false);
        return;
      }
      
      setInvoice(invoiceData);
      
      // Load payment settings
      const settingsDoc = await getDoc(doc(db, 'settings', 'payment'));
      if (settingsDoc.exists()) {
        setPaymentSettings(settingsDoc.data());
      }
    } catch (err) {
      console.error('Error loading invoice:', err);
      setError('Failed to load invoice');
    } finally {
      setLoading(false);
    }
  };
  
  // Calculate totals
  const calculateSubtotal = () => {
    if (!invoice?.lineItems) return invoice?.total || 0;
    return invoice.lineItems.reduce((sum, item) => sum + (item.quantity * item.price), 0);
  };
  
  const subtotal = calculateSubtotal();
  const laborTotal = (invoice?.laborHours || 0) * (invoice?.laborRate || 0);
  const materialsCost = invoice?.materialsCost || 0;
  const baseTotal = subtotal + laborTotal + materialsCost;
  const testimonialDiscount = wantsTestimonial ? (baseTotal * (paymentSettings?.testimonialDiscount || 10) / 100) : 0;
  const existingDiscount = invoice?.discount || 0;
  const total = baseTotal - testimonialDiscount - existingDiscount;
  
  const handlePaymentComplete = async () => {
    setProcessing(true);
    
    try {
      // Update invoice status
      await updateDoc(doc(db, 'invoices', invoiceId), {
        status: 'paid',
        paidAt: serverTimestamp(),
        paymentMethod: selectedPaymentMethod,
        hasTestimonial: wantsTestimonial,
        finalAmount: total,
      });
      
      // Save payment record
      await addDoc(collection(db, 'payments'), {
        invoiceId,
        clientId: invoice.clientId,
        clientName: invoice.clientName,
        clientEmail: invoice.clientEmail,
        jobId: invoice.jobId,
        amount: total,
        paymentMethod: selectedPaymentMethod,
        hasTestimonial: wantsTestimonial,
        status: 'completed',
        createdAt: serverTimestamp(),
      });
      
      // Update client stats if we have a client ID
      if (invoice.clientId) {
        try {
          const clientRef = doc(db, 'clients', invoice.clientId);
          const clientDoc = await getDoc(clientRef);
          if (clientDoc.exists()) {
            const clientData = clientDoc.data();
            await updateDoc(clientRef, {
              totalJobs: (clientData.totalJobs || 0) + 1,
              totalSpent: (clientData.totalSpent || 0) + total,
              lastJob: new Date().toISOString().split('T')[0],
              updatedAt: serverTimestamp(),
            });
          }
        } catch (clientErr) {
          console.log('Could not update client stats:', clientErr);
        }
      }
      
      // Update job status if we have a job ID
      if (invoice.jobId) {
        try {
          await updateDoc(doc(db, 'events', invoice.jobId), {
            status: 'completed',
            paidAt: serverTimestamp(),
            paymentAmount: total,
          });
        } catch (jobErr) {
          console.log('Could not update job status:', jobErr);
        }
      }
      
      // Add testimonial if provided
      if (wantsTestimonial && reviewText.trim()) {
        await addDoc(collection(db, 'testimonials'), {
          name: invoice.clientName || 'Anonymous',
          location: invoice.clientAddress?.split(',')[1]?.trim() || 'Las Vegas, NV',
          rating,
          text: reviewText,
          active: false, // Needs admin approval
          source: 'invoice_payment',
          clientId: invoice.clientId,
          invoiceId,
          createdAt: serverTimestamp(),
        });
      }
      
      // Try to send confirmation email
      try {
        const sendInvoiceEmail = httpsCallable(functions, 'sendInvoiceEmail');
        await sendInvoiceEmail({
          clientEmail: invoice.clientEmail,
          clientName: invoice.clientName,
          invoiceId,
          lineItems: invoice.lineItems,
          total,
        });
      } catch (emailErr) {
        console.log('Could not send confirmation email:', emailErr);
      }
      
      setStep(3); // Success
    } catch (err) {
      console.error('Error processing payment:', err);
      alert('There was an error processing your payment. Please try again.');
    } finally {
      setProcessing(false);
    }
  };
  
  // Loading state
  if (loading) {
    return (
      <View style={styles.container}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.gold} />
          <Text style={styles.loadingText}>Loading invoice...</Text>
        </View>
      </View>
    );
  }
  
  // Invoice already paid
  if (error === 'paid') {
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <View style={styles.headerLogo}>
            <Text style={styles.headerLogoText}>HANDY</Text>
            <Text style={styles.headerLogoAccent}>HAWK</Text>
          </View>
        </View>
        
        <View style={styles.errorContainer}>
          <View style={styles.errorIconWrap}>
            <Icon name="check-circle" size={64} color={colors.green} />
          </View>
          <Text style={styles.errorTitle}>Invoice Already Paid</Text>
          <Text style={styles.errorSubtitle}>
            This invoice has already been paid and is no longer available for payment.
          </Text>
          
          {invoice && (
            <View style={styles.paidInvoiceDetails}>
              <Text style={styles.paidInvoiceLabel}>Invoice #{invoiceId?.slice(-8).toUpperCase()}</Text>
              <Text style={styles.paidInvoiceAmount}>${invoice.finalAmount || invoice.total}</Text>
              <Text style={styles.paidInvoiceDate}>
                Paid on {invoice.paidAt?.toDate ? invoice.paidAt.toDate().toLocaleDateString() : 'N/A'}
              </Text>
            </View>
          )}
          
          <Text style={styles.errorContact}>
            If you have questions, contact us at (702) 936-9959
          </Text>
        </View>
        
        <View style={styles.footer}>
          <Text style={styles.footerText}>HandyHawk • Las Vegas, Nevada</Text>
        </View>
      </View>
    );
  }
  
  // Invoice invalid or not found
  if (error) {
    return (
      <View style={styles.container}>
        <View style={styles.header}>
          <View style={styles.headerLogo}>
            <Text style={styles.headerLogoText}>HANDY</Text>
            <Text style={styles.headerLogoAccent}>HAWK</Text>
          </View>
        </View>
        
        <View style={styles.errorContainer}>
          <View style={styles.errorIconWrap}>
            <Icon name="alert-circle" size={64} color={colors.red} />
          </View>
          <Text style={styles.errorTitle}>Invoice Not Available</Text>
          <Text style={styles.errorSubtitle}>
            {error === 'invalid' 
              ? 'This invoice is no longer valid and cannot be paid.'
              : 'We couldn\'t find this invoice. It may have been removed or the link is incorrect.'}
          </Text>
          <Text style={styles.errorContact}>
            If you believe this is an error, please contact us at (702) 936-9959
          </Text>
        </View>
        
        <View style={styles.footer}>
          <Text style={styles.footerText}>HandyHawk • Las Vegas, Nevada</Text>
        </View>
      </View>
    );
  }
  
  // Get payment method info for display
  const getPaymentMethodInfo = (method) => {
    const methods = {
      venmo: { name: 'Venmo', handle: paymentSettings?.venmoHandle || '@HandyHawk', icon: 'smartphone', color: '#3D95CE' },
      cashapp: { name: 'Cash App', handle: paymentSettings?.cashappHandle || '$HandyHawk', icon: 'dollar-sign', color: '#00D632' },
      zelle: { name: 'Zelle', handle: paymentSettings?.zelleEmail || 'payments@handyhawk.net', icon: 'home', color: '#6D1ED4' },
      cash: { name: 'Cash', handle: 'Pay in person', icon: 'dollar-sign', color: colors.green },
      check: { name: 'Check', handle: 'Pay by check', icon: 'file-text', color: colors.orange },
    };
    
    // Add custom payment methods
    if (paymentSettings?.customPaymentMethods) {
      paymentSettings.customPaymentMethods.forEach(pm => {
        methods[pm.id] = {
          name: pm.name,
          handle: pm.handle || pm.url || 'See details',
          icon: pm.icon || 'credit-card',
          color: pm.color || colors.teal,
          qrCode: pm.qrCodeUrl,
          url: pm.url,
        };
      });
    }
    
    return methods[method] || { name: method, handle: '', icon: 'credit-card', color: colors.teal };
  };
  
  // Step 1: Review Invoice
  const renderReviewStep = () => (
    <View style={styles.stepContainer}>
      <View style={styles.invoiceCard}>
        <View style={styles.invoiceHeader}>
          <View>
            <Text style={styles.invoiceTitle}>Invoice</Text>
            <Text style={styles.invoiceNumber}>#{invoiceId?.slice(-8).toUpperCase()}</Text>
          </View>
          <Text style={styles.invoiceDate}>{new Date().toLocaleDateString()}</Text>
        </View>
        
        {/* Client Info */}
        <View style={styles.invoiceClientBox}>
          <Text style={styles.invoiceClientLabel}>Bill To:</Text>
          <Text style={styles.invoiceClientName}>{invoice.clientName || 'Customer'}</Text>
          {invoice.clientEmail && <Text style={styles.invoiceClientEmail}>{invoice.clientEmail}</Text>}
        </View>
        
        {/* Line Items */}
        <View style={styles.invoiceItems}>
          <View style={styles.invoiceItemHeader}>
            <Text style={[styles.invoiceItemCol, { flex: 2 }]}>Description</Text>
            <Text style={[styles.invoiceItemCol, { flex: 0.5, textAlign: 'center' }]}>Qty</Text>
            <Text style={[styles.invoiceItemCol, { flex: 1, textAlign: 'right' }]}>Price</Text>
            <Text style={[styles.invoiceItemCol, { flex: 1, textAlign: 'right' }]}>Total</Text>
          </View>
          
          {invoice.lineItems?.map((item, idx) => (
            <View key={idx} style={styles.invoiceItemRow}>
              <Text style={[styles.invoiceItemText, { flex: 2 }]}>{item.description || item.name}</Text>
              <Text style={[styles.invoiceItemText, { flex: 0.5, textAlign: 'center' }]}>{item.quantity}</Text>
              <Text style={[styles.invoiceItemText, { flex: 1, textAlign: 'right' }]}>${item.price?.toFixed(2)}</Text>
              <Text style={[styles.invoiceItemText, { flex: 1, textAlign: 'right' }]}>${(item.quantity * item.price).toFixed(2)}</Text>
            </View>
          ))}
          
          {/* Labor if exists */}
          {laborTotal > 0 && (
            <View style={styles.invoiceItemRow}>
              <Text style={[styles.invoiceItemText, { flex: 2 }]}>Labor ({invoice.laborHours} hrs @ ${invoice.laborRate}/hr)</Text>
              <Text style={[styles.invoiceItemText, { flex: 0.5, textAlign: 'center' }]}>—</Text>
              <Text style={[styles.invoiceItemText, { flex: 1, textAlign: 'right' }]}>—</Text>
              <Text style={[styles.invoiceItemText, { flex: 1, textAlign: 'right' }]}>${laborTotal.toFixed(2)}</Text>
            </View>
          )}
          
          {/* Materials if exists */}
          {materialsCost > 0 && (
            <View style={styles.invoiceItemRow}>
              <Text style={[styles.invoiceItemText, { flex: 2 }]}>Materials</Text>
              <Text style={[styles.invoiceItemText, { flex: 0.5, textAlign: 'center' }]}>—</Text>
              <Text style={[styles.invoiceItemText, { flex: 1, textAlign: 'right' }]}>—</Text>
              <Text style={[styles.invoiceItemText, { flex: 1, textAlign: 'right' }]}>${materialsCost.toFixed(2)}</Text>
            </View>
          )}
        </View>
        
        {/* Totals */}
        <View style={styles.invoiceTotals}>
          <View style={styles.invoiceTotalRow}>
            <Text style={styles.invoiceTotalLabel}>Subtotal</Text>
            <Text style={styles.invoiceTotalValue}>${baseTotal.toFixed(2)}</Text>
          </View>
          
          {existingDiscount > 0 && (
            <View style={styles.invoiceTotalRow}>
              <Text style={[styles.invoiceTotalLabel, { color: colors.green }]}>Discount Applied</Text>
              <Text style={[styles.invoiceTotalValue, { color: colors.green }]}>-${existingDiscount.toFixed(2)}</Text>
            </View>
          )}
          
          {testimonialDiscount > 0 && (
            <View style={styles.invoiceTotalRow}>
              <Text style={[styles.invoiceTotalLabel, { color: colors.green }]}>Review Discount</Text>
              <Text style={[styles.invoiceTotalValue, { color: colors.green }]}>-${testimonialDiscount.toFixed(2)}</Text>
            </View>
          )}
          
          <View style={[styles.invoiceTotalRow, styles.invoiceTotalFinal]}>
            <Text style={styles.invoiceTotalLabelFinal}>Total Due</Text>
            <Text style={styles.invoiceTotalValueFinal}>${total.toFixed(2)}</Text>
          </View>
        </View>
      </View>
      
      {/* Testimonial Option */}
      <View style={styles.testimonialOption}>
        <View style={styles.testimonialOptionHeader}>
          <Icon name="star" size={20} color={colors.gold} />
          <Text style={styles.testimonialOptionTitle}>Leave a Review & Save!</Text>
        </View>
        <Text style={styles.testimonialOptionDesc}>
          {paymentSettings?.testimonialPrompt?.replace('{discount}', paymentSettings?.testimonialDiscount) || 
            `Leave us a review and save ${paymentSettings?.testimonialDiscount || 10}% on your bill!`}
        </Text>
        
        <View style={styles.testimonialButtons}>
          <TouchableOpacity 
            style={[styles.testimonialBtn, wantsTestimonial && styles.testimonialBtnActive]}
            onPress={() => setWantsTestimonial(true)}
          >
            <Icon name="check-circle" size={16} color={wantsTestimonial ? colors.darkNavy : colors.teal} />
            <Text style={[styles.testimonialBtnText, wantsTestimonial && styles.testimonialBtnTextActive]}>
              Yes, Save {paymentSettings?.testimonialDiscount || 10}%
            </Text>
          </TouchableOpacity>
          <TouchableOpacity 
            style={[styles.testimonialBtn, !wantsTestimonial && styles.testimonialBtnInactive]}
            onPress={() => setWantsTestimonial(false)}
          >
            <Text style={styles.testimonialBtnTextSecondary}>No Thanks</Text>
          </TouchableOpacity>
        </View>
        
        {/* Review form if wants testimonial */}
        {wantsTestimonial && (
          <View style={styles.reviewForm}>
            <View style={styles.starsRow}>
              {[1, 2, 3, 4, 5].map((star) => (
                <TouchableOpacity key={star} onPress={() => setRating(star)}>
                  <Text style={[styles.star, star <= rating && styles.starActive]}>★</Text>
                </TouchableOpacity>
              ))}
            </View>
            <TextInput
              style={styles.reviewInput}
              placeholder="Tell us about your experience..."
              placeholderTextColor={colors.gray}
              value={reviewText}
              onChangeText={setReviewText}
              multiline
              numberOfLines={3}
            />
          </View>
        )}
      </View>
      
      <TouchableOpacity 
        style={styles.primaryBtn}
        onPress={() => setStep(2)}
      >
        <Text style={styles.primaryBtnText}>Continue to Payment</Text>
        <Icon name="arrow-right" size={18} color={colors.darkNavy} />
      </TouchableOpacity>
    </View>
  );
  
  // Step 2: Payment Selection
  const renderPaymentStep = () => {
    const availableMethods = ['venmo', 'cashapp', 'zelle', 'cash', 'check'];
    
    // Add custom payment methods
    if (paymentSettings?.customPaymentMethods) {
      paymentSettings.customPaymentMethods.forEach(pm => {
        if (pm.enabled !== false) {
          availableMethods.push(pm.id);
        }
      });
    }
    
    const selectedMethodInfo = selectedPaymentMethod ? getPaymentMethodInfo(selectedPaymentMethod) : null;
    
    return (
      <View style={styles.stepContainer}>
        {/* Final Amount */}
        <View style={styles.paymentAmountCard}>
          <Text style={styles.paymentAmountLabel}>Amount Due</Text>
          <Text style={styles.paymentAmountValue}>${total.toFixed(2)}</Text>
          {testimonialDiscount > 0 && (
            <View style={styles.paymentSavings}>
              <Icon name="check-circle" size={14} color={colors.green} />
              <Text style={styles.paymentSavingsText}>You saved ${testimonialDiscount.toFixed(2)} with your review!</Text>
            </View>
          )}
        </View>
        
        {/* Payment Methods */}
        <View style={styles.paymentMethods}>
          <Text style={styles.paymentMethodsTitle}>Select Payment Method</Text>
          
          <View style={styles.paymentMethodGroup}>
            {availableMethods.map(methodKey => {
              const methodInfo = getPaymentMethodInfo(methodKey);
              
              // Check if standard method is configured
              if (methodKey === 'venmo' && !paymentSettings?.venmoHandle) return null;
              if (methodKey === 'cashapp' && !paymentSettings?.cashappHandle) return null;
              if (methodKey === 'zelle' && !paymentSettings?.zelleEmail) return null;
              
              return (
                <TouchableOpacity 
                  key={methodKey}
                  style={[styles.paymentMethodBtn, selectedPaymentMethod === methodKey && styles.paymentMethodBtnActive]}
                  onPress={() => setSelectedPaymentMethod(methodKey)}
                >
                  <View style={[styles.paymentMethodIcon, { backgroundColor: methodInfo.color + '20' }]}>
                    <Icon name={methodInfo.icon} size={20} color={methodInfo.color} />
                  </View>
                  <View style={styles.paymentMethodInfo}>
                    <Text style={styles.paymentMethodName}>{methodInfo.name}</Text>
                    <Text style={styles.paymentMethodHandle}>{methodInfo.handle}</Text>
                  </View>
                  {selectedPaymentMethod === methodKey && <Icon name="check-circle" size={20} color={colors.teal} />}
                </TouchableOpacity>
              );
            }).filter(Boolean)}
          </View>
        </View>
        
        {/* Show QR code if selected method has one */}
        {selectedMethodInfo?.qrCode && (
          <View style={styles.qrCodeSection}>
            <Text style={styles.qrCodeTitle}>Scan to Pay with {selectedMethodInfo.name}</Text>
            <Image 
              source={{ uri: selectedMethodInfo.qrCode }} 
              style={styles.qrCodeImage}
              resizeMode="contain"
            />
            {selectedMethodInfo.url && (
              <TouchableOpacity 
                style={styles.qrCodeLink}
                onPress={() => isWeb && window.open(selectedMethodInfo.url, '_blank')}
              >
                <Icon name="external-link" size={14} color={colors.teal} />
                <Text style={styles.qrCodeLinkText}>Open {selectedMethodInfo.name}</Text>
              </TouchableOpacity>
            )}
          </View>
        )}
        
        {/* Payment Instructions */}
        {selectedPaymentMethod && (
          <View style={styles.paymentInstructions}>
            <Text style={styles.paymentInstructionsTitle}>
              {selectedPaymentMethod === 'cash' || selectedPaymentMethod === 'check' 
                ? 'In-Person Payment' 
                : `Pay via ${getPaymentMethodInfo(selectedPaymentMethod).name}`}
            </Text>
            <Text style={styles.paymentInstructionsText}>
              {selectedPaymentMethod === 'cash' || selectedPaymentMethod === 'check' 
                ? 'Please pay the technician upon completion of service.'
                : `Send $${total.toFixed(2)} to ${getPaymentMethodInfo(selectedPaymentMethod).handle} and click "Complete Payment" below.`}
            </Text>
          </View>
        )}
        
        <View style={styles.stepButtons}>
          <TouchableOpacity style={styles.backBtn} onPress={() => setStep(1)}>
            <Icon name="arrow-left" size={18} color={colors.teal} />
            <Text style={styles.backBtnText}>Back</Text>
          </TouchableOpacity>
          <TouchableOpacity 
            style={[styles.primaryBtn, { flex: 1 }, !selectedPaymentMethod && styles.primaryBtnDisabled]}
            onPress={handlePaymentComplete}
            disabled={!selectedPaymentMethod || processing}
          >
            {processing ? (
              <ActivityIndicator size="small" color={colors.darkNavy} />
            ) : (
              <>
                <Text style={styles.primaryBtnText}>Complete Payment</Text>
                <Icon name="check" size={18} color={colors.darkNavy} />
              </>
            )}
          </TouchableOpacity>
        </View>
      </View>
    );
  };
  
  // Step 3: Success
  const renderSuccessStep = () => (
    <View style={styles.successContainer}>
      <View style={styles.successIcon}>
        <Icon name="check-circle" size={64} color={colors.green} />
      </View>
      <Text style={styles.successTitle}>Payment Complete!</Text>
      <Text style={styles.successSubtitle}>Thank you for choosing HandyHawk</Text>
      
      {testimonialDiscount > 0 && (
        <View style={styles.successSavingsBadge}>
          <Icon name="tag" size={16} color={colors.green} />
          <Text style={styles.successSavingsText}>You saved ${testimonialDiscount.toFixed(2)} with your review!</Text>
        </View>
      )}
      
      <View style={styles.successReceipt}>
        <Text style={styles.successReceiptTitle}>Receipt</Text>
        <View style={styles.successReceiptRow}>
          <Text style={styles.successReceiptLabel}>Invoice #</Text>
          <Text style={styles.successReceiptValue}>{invoiceId?.slice(-8).toUpperCase()}</Text>
        </View>
        <View style={styles.successReceiptRow}>
          <Text style={styles.successReceiptLabel}>Amount Paid</Text>
          <Text style={styles.successReceiptValue}>${total.toFixed(2)}</Text>
        </View>
        <View style={styles.successReceiptRow}>
          <Text style={styles.successReceiptLabel}>Payment Method</Text>
          <Text style={styles.successReceiptValue}>{getPaymentMethodInfo(selectedPaymentMethod).name}</Text>
        </View>
        <View style={styles.successReceiptRow}>
          <Text style={styles.successReceiptLabel}>Date</Text>
          <Text style={styles.successReceiptValue}>{new Date().toLocaleDateString()}</Text>
        </View>
      </View>
      
      <Text style={styles.successMessage}>
        {paymentSettings?.customMessage || 'Thank you for your business! We appreciate you choosing HandyHawk for your home improvement needs.'}
      </Text>
      
      <TouchableOpacity 
        style={styles.successCloseBtn}
        onPress={() => {
          if (isWeb) window.location.href = 'https://handyhawk.org';
        }}
      >
        <Text style={styles.successCloseBtnText}>Return to HandyHawk</Text>
      </TouchableOpacity>
    </View>
  );
  
  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.headerLogo}>
          <Text style={styles.headerLogoText}>HANDY</Text>
          <Text style={styles.headerLogoAccent}>HAWK</Text>
        </View>
        <View style={styles.stepIndicator}>
          {step < 3 && (
            <>
              <View style={[styles.stepDot, step >= 1 && styles.stepDotActive]} />
              <View style={[styles.stepDot, step >= 2 && styles.stepDotActive]} />
            </>
          )}
        </View>
      </View>
      
      {/* Content */}
      <ScrollView 
        style={styles.content} 
        contentContainerStyle={styles.contentInner}
        showsVerticalScrollIndicator={false}
      >
        <Animated.View style={{ opacity: fadeAnim }}>
          {step === 1 && renderReviewStep()}
          {step === 2 && renderPaymentStep()}
          {step === 3 && renderSuccessStep()}
        </Animated.View>
      </ScrollView>
      
      {/* Footer */}
      <View style={styles.footer}>
        <Text style={styles.footerText}>Secured by HandyHawk</Text>
        <View style={styles.footerIcons}>
          <Icon name="lock" size={14} color={colors.gray} />
          <Icon name="shield" size={14} color={colors.gray} />
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.darkNavy,
    minHeight: '100vh',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
  },
  loadingText: {
    color: colors.gray,
    fontSize: 16,
    marginTop: 16,
  },
  
  // Error states
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
    minHeight: 400,
  },
  errorIconWrap: {
    marginBottom: 24,
  },
  errorTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: colors.white,
    marginBottom: 12,
    textAlign: 'center',
  },
  errorSubtitle: {
    fontSize: 16,
    color: colors.gray,
    textAlign: 'center',
    lineHeight: 24,
    marginBottom: 24,
  },
  errorContact: {
    fontSize: 14,
    color: colors.textMuted,
    textAlign: 'center',
    marginTop: 20,
  },
  paidInvoiceDetails: {
    backgroundColor: colors.navy,
    borderRadius: 16,
    padding: 24,
    alignItems: 'center',
    marginTop: 20,
    borderWidth: 1,
    borderColor: colors.glassBorder,
  },
  paidInvoiceLabel: {
    fontSize: 12,
    color: colors.gray,
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 8,
  },
  paidInvoiceAmount: {
    fontSize: 32,
    fontWeight: '700',
    color: colors.green,
    marginBottom: 8,
  },
  paidInvoiceDate: {
    fontSize: 14,
    color: colors.textMuted,
  },
  
  // Header
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    backgroundColor: colors.navy,
    borderBottomWidth: 1,
    borderBottomColor: colors.glassBorder,
  },
  headerLogo: {
    flexDirection: 'row',
    alignItems: 'baseline',
  },
  headerLogoText: {
    fontSize: 20,
    fontWeight: '800',
    color: colors.white,
  },
  headerLogoAccent: {
    fontSize: 20,
    fontWeight: '800',
    color: colors.gold,
  },
  stepIndicator: {
    flexDirection: 'row',
    gap: 8,
  },
  stepDot: {
    width: 10,
    height: 10,
    borderRadius: 5,
    backgroundColor: colors.navyLight,
  },
  stepDotActive: {
    backgroundColor: colors.gold,
  },
  
  // Content
  content: {
    flex: 1,
  },
  contentInner: {
    padding: 20,
    maxWidth: 600,
    alignSelf: 'center',
    width: '100%',
  },
  stepContainer: {
    gap: 20,
  },
  
  // Invoice Card
  invoiceCard: {
    backgroundColor: colors.navy,
    borderRadius: 16,
    padding: 24,
    borderWidth: 1,
    borderColor: colors.glassBorder,
  },
  invoiceHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 20,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.glassBorder,
  },
  invoiceTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: colors.white,
  },
  invoiceNumber: {
    fontSize: 14,
    color: colors.gold,
    marginTop: 4,
  },
  invoiceDate: {
    fontSize: 14,
    color: colors.gray,
  },
  invoiceClientBox: {
    marginBottom: 20,
    padding: 16,
    backgroundColor: colors.navyLight,
    borderRadius: 10,
  },
  invoiceClientLabel: {
    fontSize: 11,
    color: colors.gray,
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 4,
  },
  invoiceClientName: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.white,
  },
  invoiceClientEmail: {
    fontSize: 14,
    color: colors.textMuted,
    marginTop: 4,
  },
  invoiceItems: {
    marginBottom: 20,
  },
  invoiceItemHeader: {
    flexDirection: 'row',
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: colors.glassBorder,
    marginBottom: 8,
  },
  invoiceItemCol: {
    fontSize: 11,
    color: colors.gray,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  invoiceItemRow: {
    flexDirection: 'row',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: colors.glassBorder + '50',
  },
  invoiceItemText: {
    fontSize: 14,
    color: colors.text,
  },
  invoiceTotals: {
    borderTopWidth: 2,
    borderTopColor: colors.glassBorder,
    paddingTop: 16,
    marginTop: 8,
  },
  invoiceTotalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 8,
  },
  invoiceTotalLabel: {
    fontSize: 14,
    color: colors.gray,
  },
  invoiceTotalValue: {
    fontSize: 14,
    color: colors.white,
    fontWeight: '500',
  },
  invoiceTotalFinal: {
    borderTopWidth: 1,
    borderTopColor: colors.glassBorder,
    marginTop: 8,
    paddingTop: 16,
  },
  invoiceTotalLabelFinal: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.white,
  },
  invoiceTotalValueFinal: {
    fontSize: 24,
    fontWeight: '700',
    color: colors.gold,
  },
  
  // Testimonial Option
  testimonialOption: {
    backgroundColor: colors.gold + '15',
    borderRadius: 16,
    padding: 20,
    borderWidth: 1,
    borderColor: colors.gold + '30',
  },
  testimonialOptionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 8,
  },
  testimonialOptionTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.gold,
  },
  testimonialOptionDesc: {
    fontSize: 14,
    color: colors.text,
    marginBottom: 16,
    lineHeight: 22,
  },
  testimonialButtons: {
    flexDirection: 'row',
    gap: 12,
  },
  testimonialBtn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    paddingVertical: 14,
    borderRadius: 10,
    backgroundColor: colors.teal + '20',
    borderWidth: 1,
    borderColor: colors.teal + '40',
  },
  testimonialBtnActive: {
    backgroundColor: colors.teal,
    borderColor: colors.teal,
  },
  testimonialBtnInactive: {
    backgroundColor: 'transparent',
    borderColor: colors.gray + '40',
  },
  testimonialBtnText: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.teal,
  },
  testimonialBtnTextActive: {
    color: colors.darkNavy,
  },
  testimonialBtnTextSecondary: {
    fontSize: 14,
    color: colors.gray,
  },
  reviewForm: {
    marginTop: 16,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: colors.gold + '30',
  },
  starsRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 8,
    marginBottom: 12,
  },
  star: {
    fontSize: 32,
    color: colors.navyLight,
  },
  starActive: {
    color: colors.gold,
  },
  reviewInput: {
    backgroundColor: colors.navy,
    borderRadius: 10,
    padding: 14,
    color: colors.white,
    fontSize: 14,
    minHeight: 80,
    textAlignVertical: 'top',
    borderWidth: 1,
    borderColor: colors.glassBorder,
  },
  
  // Primary Button
  primaryBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    backgroundColor: colors.gold,
    paddingVertical: 16,
    borderRadius: 12,
  },
  primaryBtnText: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.darkNavy,
  },
  primaryBtnDisabled: {
    opacity: 0.5,
  },
  
  // Back Button
  backBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 16,
    paddingHorizontal: 20,
    borderRadius: 12,
    backgroundColor: colors.teal + '15',
    borderWidth: 1,
    borderColor: colors.teal + '30',
  },
  backBtnText: {
    fontSize: 15,
    fontWeight: '600',
    color: colors.teal,
  },
  stepButtons: {
    flexDirection: 'row',
    gap: 12,
  },
  
  // Payment
  paymentAmountCard: {
    backgroundColor: colors.navy,
    borderRadius: 16,
    padding: 24,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.glassBorder,
  },
  paymentAmountLabel: {
    fontSize: 14,
    color: colors.gray,
    marginBottom: 8,
  },
  paymentAmountValue: {
    fontSize: 48,
    fontWeight: '700',
    color: colors.gold,
  },
  paymentSavings: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 12,
    backgroundColor: colors.green + '15',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
  },
  paymentSavingsText: {
    fontSize: 13,
    color: colors.green,
  },
  paymentMethods: {
    backgroundColor: colors.navy,
    borderRadius: 16,
    padding: 20,
    borderWidth: 1,
    borderColor: colors.glassBorder,
  },
  paymentMethodsTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.white,
    marginBottom: 16,
  },
  paymentMethodGroup: {
    gap: 10,
  },
  paymentMethodBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    backgroundColor: colors.navyLight,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: colors.glassBorder,
    gap: 14,
  },
  paymentMethodBtnActive: {
    borderColor: colors.teal,
    backgroundColor: colors.teal + '10',
  },
  paymentMethodIcon: {
    width: 44,
    height: 44,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
  },
  paymentMethodEmoji: {
    fontSize: 22,
  },
  paymentMethodInfo: {
    flex: 1,
  },
  paymentMethodName: {
    fontSize: 15,
    fontWeight: '600',
    color: colors.white,
  },
  paymentMethodHandle: {
    fontSize: 13,
    color: colors.gray,
    marginTop: 2,
  },
  paymentInstructions: {
    backgroundColor: colors.teal + '15',
    borderRadius: 12,
    padding: 16,
    borderLeftWidth: 3,
    borderLeftColor: colors.teal,
  },
  paymentInstructionsTitle: {
    color: colors.white,
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 4,
  },
  paymentInstructionsText: {
    color: colors.gray,
    fontSize: 13,
    lineHeight: 20,
  },
  
  // QR Code Section
  qrCodeSection: {
    backgroundColor: colors.navy,
    borderRadius: 16,
    padding: 24,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.glassBorder,
  },
  qrCodeTitle: {
    fontSize: 14,
    color: colors.gray,
    marginBottom: 16,
  },
  qrCodeImage: {
    width: 200,
    height: 200,
    backgroundColor: colors.white,
    borderRadius: 12,
  },
  qrCodeLink: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 16,
    paddingVertical: 10,
    paddingHorizontal: 16,
    backgroundColor: colors.teal + '15',
    borderRadius: 8,
  },
  qrCodeLinkText: {
    color: colors.teal,
    fontSize: 14,
    fontWeight: '500',
  },
  
  // Success
  successContainer: {
    alignItems: 'center',
    paddingVertical: 40,
  },
  successIcon: {
    marginBottom: 24,
  },
  successTitle: {
    fontSize: 28,
    fontWeight: '700',
    color: colors.white,
    marginBottom: 8,
  },
  successSubtitle: {
    fontSize: 16,
    color: colors.gray,
    marginBottom: 24,
  },
  successSavingsBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: colors.green + '15',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 20,
    marginBottom: 24,
  },
  successSavingsText: {
    fontSize: 14,
    color: colors.green,
    fontWeight: '500',
  },
  successReceipt: {
    width: '100%',
    backgroundColor: colors.navy,
    borderRadius: 16,
    padding: 20,
    marginBottom: 24,
    borderWidth: 1,
    borderColor: colors.glassBorder,
  },
  successReceiptTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.gray,
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 16,
    textAlign: 'center',
  },
  successReceiptRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 10,
    borderBottomWidth: 1,
    borderBottomColor: colors.glassBorder,
  },
  successReceiptLabel: {
    fontSize: 14,
    color: colors.gray,
  },
  successReceiptValue: {
    fontSize: 14,
    color: colors.white,
    fontWeight: '500',
    textTransform: 'capitalize',
  },
  successMessage: {
    fontSize: 14,
    color: colors.textMuted,
    textAlign: 'center',
    lineHeight: 22,
    marginBottom: 24,
    paddingHorizontal: 20,
  },
  successCloseBtn: {
    paddingVertical: 14,
    paddingHorizontal: 32,
    backgroundColor: colors.gold,
    borderRadius: 10,
  },
  successCloseBtnText: {
    fontSize: 15,
    color: colors.darkNavy,
    fontWeight: '600',
  },
  
  // Footer
  footer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 12,
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: colors.glassBorder,
    backgroundColor: colors.navy,
  },
  footerText: {
    fontSize: 12,
    color: colors.gray,
  },
  footerIcons: {
    flexDirection: 'row',
    gap: 8,
  },
});

export default InvoicePayScreen;

